import type { OnlineItemResponse } from '../types/chapterSupplies';

const CART_LEGACY_KEY = 'chapter_supplies_storefront_cart_v1';
const CART_KEY_V2 = 'chapter_supplies_storefront_cart_v2';
const CART_KEY = 'chapter_supplies_storefront_cart_v3';
const PERSONALIZATION_NAME_MAX_CHARS = 255;

/** Unicode code-point count — aligns roughly with Go `utf8.RuneCountInString` for user-facing limits. */
export const storefrontTextCharCount = (s: string): number => [...s].length;

function newLineId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `line_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function clipToMaxChars(val: string, max: number): string {
  return [...val].slice(0, max).join('');
}

export interface ChapterSuppliesCartItem {
  line_id: string;
  online_item_id: string;
  /** Set when parent online item uses variants (`has_variants`). */
  online_item_variant_id?: string | null;
  variant_label?: string | null;
  name: string;
  unit_price: number;
  quantity: number;
  image_url?: string;
  variant_image_url?: string;
  parent_image_url?: string;
  child_image_url?: string;
  order_approval_required?: boolean;
  no_shipping_charge?: number;
  custom_text_lines: string[];
  /** Mirrors server expectation: trimmed non-empty lines required at checkout. */
  customization_required?: boolean;
  max_text_lines?: number | null;
  max_text_length?: number | null;
  personalization_user_id?: string | null;
  personalization_display_name?: string | null;
  /** Editable engraved / display text (sent as `personalization_first_name` in the checkout API). */
  personalization_full_name?: string | null;
  personalization_required?: boolean;
}

export type AddToCartOptions = {
  quantity?: number;
  online_item_variant_id?: string | null;
  variant_label?: string | null;
  /** Resolved display price (variant or parent). */
  unit_price: number;
  custom_text_lines?: string[];
  customization_required?: boolean;
  max_text_lines?: number | null;
  max_text_length?: number | null;
  personalization_user_id?: string | null;
  personalization_display_name?: string | null;
  personalization_full_name?: string | null;
  personalization_required?: boolean;
  image_url?: string;
  variant_image_url?: string;
  parent_image_url?: string;
  child_image_url?: string;
};

export function storefrontItemNeedsConfiguration(
  item: OnlineItemResponse
): boolean {
  const hasVariants = (item.has_variants ?? 0) !== 0;
  const allowsCustom =
    (item.allows_custom_text ?? 0) !== 0 ||
    (item.allows_personalization ?? 0) !== 0;
  return hasVariants || allowsCustom;
}

function migrateFromV1(parsed: unknown): ChapterSuppliesCartItem[] {
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((row: Record<string, unknown>) => ({
      line_id: newLineId(),
      online_item_id: String(row.online_item_id ?? ''),
      name: String(row.name ?? ''),
      unit_price: Number(row.unit_price ?? 0),
      quantity: Math.max(1, Math.floor(Number(row.quantity ?? 1))),
      image_url: row.image_url ? String(row.image_url) : undefined,
      order_approval_required: Boolean(row.order_approval_required),
      custom_text_lines: [],
      customization_required: false,
    }))
    .filter((row) => row.online_item_id);
}

function normalizeCartRow(
  row: Record<string, unknown>
): ChapterSuppliesCartItem {
  const lineId = row.line_id != null ? String(row.line_id) : newLineId();
  const custom = Array.isArray(row.custom_text_lines)
    ? (row.custom_text_lines as unknown[]).map((cell) => String(cell ?? ''))
    : [];
  const legacyFirst =
    row.personalization_first_name != null
      ? String(row.personalization_first_name)
      : null;
  const fullName =
    row.personalization_full_name != null
      ? String(row.personalization_full_name)
      : legacyFirst;
  return {
    line_id: lineId,
    online_item_id: String(row.online_item_id ?? ''),
    online_item_variant_id:
      row.online_item_variant_id != null
        ? String(row.online_item_variant_id)
        : null,
    variant_label: row.variant_label != null ? String(row.variant_label) : null,
    name: String(row.name ?? ''),
    unit_price: Number(row.unit_price ?? 0),
    quantity: Math.max(1, Math.floor(Number(row.quantity ?? 1))),
    image_url: row.image_url ? String(row.image_url) : undefined,
    variant_image_url: row.variant_image_url
      ? String(row.variant_image_url)
      : undefined,
    parent_image_url: row.parent_image_url
      ? String(row.parent_image_url)
      : undefined,
    child_image_url: row.child_image_url
      ? String(row.child_image_url)
      : undefined,
    order_approval_required: Boolean(row.order_approval_required),
    no_shipping_charge:
      row.no_shipping_charge != null ? Number(row.no_shipping_charge) : 0,
    custom_text_lines: custom,
    customization_required: Boolean(row.customization_required),
    max_text_lines:
      row.max_text_lines != null ? Number(row.max_text_lines) : null,
    max_text_length:
      row.max_text_length != null ? Number(row.max_text_length) : null,
    personalization_user_id:
      row.personalization_user_id != null
        ? String(row.personalization_user_id)
        : null,
    personalization_display_name:
      row.personalization_display_name != null
        ? String(row.personalization_display_name)
        : null,
    personalization_full_name:
      fullName != null && fullName !== '' ? fullName : null,
    personalization_required: Boolean(row.personalization_required),
  };
}

export const getCart = (): ChapterSuppliesCartItem[] => {
  try {
    let raw = localStorage.getItem(CART_KEY);
    if (!raw) {
      raw = localStorage.getItem(CART_KEY_V2);
      if (raw) {
        localStorage.setItem(CART_KEY, raw);
        localStorage.removeItem(CART_KEY_V2);
      }
    }
    if (!raw) {
      const legacy = localStorage.getItem(CART_LEGACY_KEY);
      if (legacy) {
        const migrated = migrateFromV1(JSON.parse(legacy));
        localStorage.setItem(CART_KEY, JSON.stringify(migrated));
        localStorage.removeItem(CART_LEGACY_KEY);
        return migrated;
      }
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const normalized: ChapterSuppliesCartItem[] = parsed
      .map((row: Record<string, unknown>) => normalizeCartRow(row))
      .filter((row) => row.online_item_id);

    localStorage.setItem(CART_KEY, JSON.stringify(normalized));
    return normalized;
  } catch {
    return [];
  }
};

const persistCart = (items: ChapterSuppliesCartItem[]) => {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
};

/** Keep engraved name as custom line 1 for checkout when this line uses member personalization. */
function withSyncedEngraving(
  line: ChapterSuppliesCartItem
): ChapterSuppliesCartItem {
  if (!line.personalization_required || !line.customization_required)
    return line;
  const cap = Math.max(1, line.max_text_lines ?? 1);
  const maxChars = PERSONALIZATION_NAME_MAX_CHARS;
  const engraved = clipToMaxChars(
    (line.personalization_full_name ?? '').trim(),
    maxChars
  );
  const nextLines = [...(line.custom_text_lines ?? [])];
  while (nextLines.length < cap) nextLines.push('');
  nextLines[0] = engraved;
  return { ...line, custom_text_lines: nextLines.slice(0, cap) };
}

export const getCartCount = (): number =>
  getCart().reduce((sum, i) => sum + Math.max(i.quantity || 0, 0), 0);

export const getCartSubtotal = (): number =>
  getCart().reduce(
    (sum, i) => sum + (i.unit_price || 0) * (i.quantity || 0),
    0
  );

function padCustomLines(
  lines: string[] | undefined,
  maxLines: number
): string[] {
  const base = [...(lines ?? [])];
  while (base.length < maxLines) base.push('');
  return base.slice(0, maxLines);
}

/**
 * Add a storefront line as its own cart row (same `online_item_id` may appear multiple times).
 * Pass `quantity` alone for backwards compatibility, or an options object from the detail page.
 */
export const addItemToCart = (
  item: OnlineItemResponse,
  quantityOrOpts: number | AddToCartOptions = 1
) => {
  const opts: AddToCartOptions =
    typeof quantityOrOpts === 'number'
      ? {
          quantity: quantityOrOpts,
          unit_price: item.price ?? 0,
          custom_text_lines: [],
          customization_required: false,
        }
      : quantityOrOpts;

  const cart = getCart();
  const quantity = Math.max(1, Math.floor(opts.quantity ?? 1));
  const sortedImages = item.images
    ?.slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const parentImageURL = opts.parent_image_url ?? sortedImages?.[0]?.image_url;
  const childImageURL =
    opts.child_image_url ??
    [...(item.variants ?? [])]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((v) => v.item_image_url?.trim())
      .find(Boolean);
  const variantImageURL = opts.variant_image_url;
  const imageURL =
    opts.image_url ?? variantImageURL ?? parentImageURL ?? childImageURL;

  const customizationRequired = Boolean(opts.customization_required);
  let maxLines = opts.max_text_lines ?? null;
  const maxChars = opts.max_text_length ?? null;
  let customLines = opts.custom_text_lines ?? [];

  if (customizationRequired) {
    const cap = Math.max(1, maxLines ?? 1);
    customLines = padCustomLines(customLines, cap);
  } else {
    customLines = [];
  }

  let row: ChapterSuppliesCartItem = {
    line_id: newLineId(),
    online_item_id: item.id,
    online_item_variant_id: opts.online_item_variant_id ?? null,
    variant_label: opts.variant_label ?? null,
    name: item.name || item.item_name || 'Untitled Item',
    unit_price: opts.unit_price,
    quantity,
    image_url: imageURL,
    variant_image_url: variantImageURL,
    parent_image_url: parentImageURL,
    child_image_url: childImageURL,
    order_approval_required: item.order_approval_required === 1,
    no_shipping_charge: item.no_shipping_charge ?? 0,
    custom_text_lines: customLines,
    customization_required: customizationRequired,
    max_text_lines: maxLines,
    max_text_length: maxChars,
    personalization_user_id: opts.personalization_user_id ?? null,
    personalization_display_name: opts.personalization_display_name ?? null,
    personalization_full_name: opts.personalization_full_name ?? null,
    personalization_required: Boolean(opts.personalization_required),
  };
  row = withSyncedEngraving(row);
  cart.push(row);
  persistCart(cart);
};

export const cartLineImageUrl = (line: ChapterSuppliesCartItem): string => {
  return (
    line.variant_image_url?.trim() ||
    line.parent_image_url?.trim() ||
    line.child_image_url?.trim() ||
    line.image_url?.trim() ||
    ''
  );
};

export const updateCartItemQuantity = (lineId: string, quantity: number) => {
  const cart = getCart();
  const nextQty = Math.max(0, Math.floor(quantity));
  const idx = cart.findIndex((c) => c.line_id === lineId);
  if (idx < 0) return;
  if (nextQty === 0) {
    cart.splice(idx, 1);
  } else {
    cart[idx] = { ...cart[idx], quantity: nextQty };
  }
  persistCart(cart);
};

export const removeCartItem = (lineId: string) => {
  persistCart(getCart().filter((i) => i.line_id !== lineId));
};

export const setCartLineCustomText = (
  lineId: string,
  lineIndex: number,
  value: string
) => {
  const cart = getCart();
  const idx = cart.findIndex((c) => c.line_id === lineId);
  if (idx < 0) return;
  const line = cart[idx];
  if (!line.customization_required) return;
  if (line.personalization_required && lineIndex === 0) {
    return;
  }
  const cap = Math.max(1, line.max_text_lines ?? 1);
  const nextLines = [...(line.custom_text_lines ?? [])];
  while (nextLines.length < cap) nextLines.push('');
  nextLines[lineIndex] = value;
  let updated: ChapterSuppliesCartItem = {
    ...line,
    custom_text_lines: nextLines.slice(0, cap),
  };
  updated = withSyncedEngraving(updated);
  cart[idx] = updated;
  persistCart(cart);
};

export const setCartLinePersonalization = (
  lineId: string,
  payload: {
    personalization_user_id?: string | null;
    personalization_display_name?: string | null;
    personalization_full_name?: string | null;
  }
) => {
  const cart = getCart();
  const idx = cart.findIndex((c) => c.line_id === lineId);
  if (idx < 0) return;
  const line = cart[idx];
  let next: ChapterSuppliesCartItem = {
    ...line,
    personalization_user_id: payload.personalization_user_id ?? null,
    personalization_display_name: payload.personalization_display_name ?? null,
    personalization_full_name: payload.personalization_full_name ?? null,
  };
  next = withSyncedEngraving(next);
  cart[idx] = next;
  persistCart(cart);
};

export const clearCart = () => persistCart([]);
